/** Ranking PROPIO del organizador (Eduardo 2026-09-22): helpers puros de la
 * pantalla "Mi ranking" (reglas, CSV de puntos iniciales, etiquetas). */

export interface CustomRankingBonus {
  champion: number;
  finalist: number;
  sf: number;
  qf: number;
  r16: number;
  r32: number;
}

export type Tiebreaker = "wins" | "sets_diff" | "games_diff" | "head_to_head";

export interface CustomRankingRules {
  match_win: number;
  match_loss: number;
  walkover_win: number;
  walkover_loss: number;
  /** null = igual que un partido jugado. */
  retired_win: number | null;
  retired_loss: number | null;
  defaulted_win: number | null;
  defaulted_loss: number | null;
  set_win: number;
  set_loss: number;
  game_win: number;
  game_loss: number;
  tiebreak_games: number;
  super_tiebreak_as: "set" | "games";
  super_tiebreak_games: number;
  bonus: CustomRankingBonus;
  /** Bono por lugar final del grupo en Round Robin, por tamaño de grupo:
   * { "3": [1º, 2º, 3º], "4": [1º, 2º, 3º, 4º] } (Eduardo 2026-09-24). */
  group_bonus: Record<string, number[]>;
  participation_bonus: number;
  undefeated_bonus: number;
  tiebreakers: Tiebreaker[];
}

/** Tamaños de grupo que se capturan en la pantalla. */
export const GROUP_BONUS_SIZES = [3, 4, 5, 6] as const;

export function groupBonusValue(rules: Pick<CustomRankingRules, "group_bonus">, size: number, index: number): number {
  return rules.group_bonus?.[String(size)]?.[index] ?? 0;
}

export function withGroupBonus(rules: CustomRankingRules, size: number, index: number, value: number): CustomRankingRules {
  const current = [...(rules.group_bonus?.[String(size)] ?? [])];
  while (current.length < size) current.push(0);
  current[index] = Math.max(Math.round(value) || 0, 0);
  const next = { ...(rules.group_bonus ?? {}) };
  if (current.some((points) => points > 0)) next[String(size)] = current.slice(0, size);
  else delete next[String(size)];
  return { ...rules, group_bonus: next };
}

export const DEFAULT_CUSTOM_RULES: CustomRankingRules = {
  match_win: 10,
  match_loss: 0,
  walkover_win: 10,
  walkover_loss: 0,
  retired_win: null,
  retired_loss: null,
  defaulted_win: null,
  defaulted_loss: null,
  set_win: 0,
  set_loss: 0,
  game_win: 0,
  game_loss: 0,
  tiebreak_games: 1,
  super_tiebreak_as: "set",
  super_tiebreak_games: 1,
  bonus: { champion: 50, finalist: 30, sf: 15, qf: 5, r16: 0, r32: 0 },
  group_bonus: {},
  participation_bonus: 0,
  undefeated_bonus: 0,
  tiebreakers: ["wins", "sets_diff", "games_diff", "head_to_head"],
};

export type IntRuleKey = "match_win" | "match_loss" | "walkover_win" | "walkover_loss" | "participation_bonus" | "undefeated_bonus" | "tiebreak_games" | "super_tiebreak_games";
export type DecimalRuleKey = "set_win" | "set_loss" | "game_win" | "game_loss";
export type OptionalRuleKey = "retired_win" | "retired_loss" | "defaulted_win" | "defaulted_loss";

export const RULE_LABELS: Array<[IntRuleKey, string, string]> = [
  ["match_win", "Partido ganado", "Suma al ganador de cada partido, en cualquier formato."],
  ["match_loss", "Partido perdido", "Puede ser negativo para que la derrota reste."],
  ["walkover_win", "Ganado por W.O.", "Cuando el rival no se presenta."],
  ["walkover_loss", "Perdido por W.O.", "Normalmente igual o más castigo que una derrota jugada."],
];

export const SCORE_RULE_LABELS: Array<[DecimalRuleKey, string, string]> = [
  ["set_win", "Set ganado", "Por cada set ganado (admite decimales)."],
  ["set_loss", "Set perdido", "Por cada set perdido (puede ser negativo)."],
  ["game_win", "Game ganado", "Por cada game ganado, p. ej. 0.2."],
  ["game_loss", "Game perdido", "Por cada game perdido (puede ser negativo)."],
];

export const OPTIONAL_RULE_LABELS: Array<[OptionalRuleKey, string, string]> = [
  ["retired_win", "Ganado por retiro", "El rival se retiró durante el partido."],
  ["retired_loss", "Perdido por retiro", "Se retiró durante el partido."],
  ["defaulted_win", "Ganado por descalificación", "El rival fue descalificado."],
  ["defaulted_loss", "Perdido por descalificación", "Fue descalificado."],
];

export const TIEBREAKER_LABELS: Record<Tiebreaker, string> = {
  wins: "Partidos ganados",
  sets_diff: "Diferencia de sets",
  games_diff: "Diferencia de games",
  head_to_head: "Enfrentamiento directo",
};

/** Completa un objeto de reglas (p. ej. de un sistema guardado antes del
 * ranking avanzado) con los defaults, sin perder lo guardado. */
export function withRuleDefaults(rules: Partial<CustomRankingRules> | null | undefined): CustomRankingRules {
  const base = rules ?? {};
  return {
    ...DEFAULT_CUSTOM_RULES,
    ...base,
    bonus: { ...DEFAULT_CUSTOM_RULES.bonus, ...(base.bonus ?? {}) },
    group_bonus: base.group_bonus ?? {},
    tiebreakers: base.tiebreakers && base.tiebreakers.length > 0 ? base.tiebreakers : DEFAULT_CUSTOM_RULES.tiebreakers,
  };
}

export const BONUS_LABELS: Array<[keyof CustomRankingBonus, string]> = [
  ["champion", "Campeón"],
  ["finalist", "Finalista"],
  ["sf", "Semifinalista"],
  ["qf", "Cuartos de final"],
  ["r16", "Octavos de final"],
  ["r32", "Dieciseisavos"],
];

export const INITIAL_POINTS_LAYOUT = "nombre,email,celular,puntos";

export interface InitialPointsRow {
  name: string | null;
  email: string | null;
  phone: string | null;
  points: number;
}

/** Convierte filas del CSV (encabezados en minúsculas) a renglones de puntos
 * iniciales. Acepta `puntos`/`points`, `nombre`/`name`, `email`/`correo`,
 * `celular`/`telefono`/`phone`. Renglones sin puntos válidos se descartan. */
export function parseInitialPointsRows(rows: Array<Record<string, string>>): InitialPointsRow[] {
  const pick = (row: Record<string, string>, keys: string[]): string | null => {
    for (const key of keys) {
      const value = (row[key] ?? "").trim();
      if (value) return value;
    }
    return null;
  };
  const result: InitialPointsRow[] = [];
  for (const row of rows) {
    const rawPoints = pick(row, ["puntos", "points", "pts"]);
    const points = rawPoints === null ? Number.NaN : Number(rawPoints.replace(/[^0-9.-]/g, ""));
    const name = pick(row, ["nombre", "name", "jugador"]);
    const email = pick(row, ["email", "correo", "mail"]);
    const phone = pick(row, ["celular", "telefono", "teléfono", "phone", "whatsapp"]);
    if (!Number.isFinite(points) || (!name && !email && !phone)) continue;
    result.push({ name, email, phone, points: Math.round(points) });
  }
  return result;
}

/** Texto corto de las reglas para listados ("+10 / -2 · campeón +50"). */
export function describeRules(
  rules: Pick<CustomRankingRules, "match_win" | "match_loss"> & { bonus: { champion: number } } | null | undefined,
): string {
  if (!rules) return "";
  const signed = (value: number) => (value > 0 ? `+${value}` : `${value}`);
  const parts = [`ganado ${signed(rules.match_win)}`, `perdido ${signed(rules.match_loss)}`];
  if (rules.bonus.champion) parts.push(`campeón ${signed(rules.bonus.champion)}`);
  return parts.join(" · ");
}
